import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { canEditMaster, useAuth } from "@/lib/auth";
import DataTable, { EmptyState } from "@/components/app/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Generic Master CRUD page.
 * Props:
 *  - endpoint: string ("/products")
 *  - title: string
 *  - description?: string
 *  - fields: [{ key, label, type: 'text'|'number'|'select'|'checkbox'|'textarea', options?, required? }]
 *  - columns: DataTable columns (optional - defaults derived from fields)
 */
export default function MasterCrud({ endpoint, title, description, fields, columns, extraColumns, extraToolbar }) {
  const { user } = useAuth();
  const canEdit = canEditMaster(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openNew = () => {
    const empty = {};
    fields.forEach((f) => {
      if (f.type === "checkbox") empty[f.key] = f.default ?? false;
      else if (f.type === "number") empty[f.key] = f.default ?? 0;
      else empty[f.key] = f.default ?? "";
    });
    setEditing(null); setForm(empty); setOpen(true);
  };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numbers
      fields.forEach((f) => {
        if (f.type === "number") payload[f.key] = payload[f.key] === "" || payload[f.key] == null ? 0 : Number(payload[f.key]);
        if (f.type === "checkbox") payload[f.key] = !!payload[f.key];
      });
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, payload);
        toast.success("Data berhasil diperbarui");
      } else {
        await api.post(endpoint, payload);
        toast.success("Data berhasil ditambahkan");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus "${row.name || row.code}"?`)) return;
    try { await api.delete(`${endpoint}/${row.id}`); toast.success("Data dihapus"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const defaultColumns = columns || fields
    .filter((f) => f.showInTable !== false)
    .map((f) => ({ key: f.key, header: f.label, render: (r) => {
      const v = r[f.key];
      if (f.type === "checkbox") return v ? <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Ya</span> : <span className="text-slate-400">-</span>;
      if (f.type === "number") return Number(v || 0).toLocaleString("id-ID");
      return v ?? "-";
    }}));

  const finalColumns = [
    ...defaultColumns,
    ...(extraColumns || []),
    ...(canEdit ? [{ key: "_actions", header: "Aksi", render: (r) => (
      <div className="flex items-center gap-1 justify-end">
        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700" title="Edit" data-testid={`btn-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" title="Hapus" data-testid={`btn-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    )}] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="gc-label">Master Data</span>
          <h1 className="gc-h1 mt-1">{title}</h1>
          {description && <p className="mt-2 text-slate-500 max-w-2xl">{description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {extraToolbar}
          {canEdit && (
            <button onClick={openNew} className="gc-btn-primary" data-testid={`btn-new-${endpoint}`}><Plus className="w-4 h-4" /> Tambah</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="gc-card"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Memuat…</div></div>
      ) : (
        <DataTable
          columns={finalColumns}
          rows={rows}
          empty={<EmptyState title="Belum ada data" desc="Mulai dengan menambahkan data baru." action={canEdit ? <button onClick={openNew} className="gc-btn-primary"><Plus className="w-4 h-4" /> Tambah</button> : null} />}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl">{editing ? "Edit" : "Tambah"} {title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="gc-label block mb-1.5">{f.label}{f.required && " *"}</label>
                {f.type === "textarea" ? (
                  <textarea rows={3} className="gc-textarea" required={f.required} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`input-${f.key}`} />
                ) : f.type === "select" ? (
                  <select className="gc-input" required={f.required} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`input-${f.key}`}>
                    <option value="">— Pilih —</option>
                    {(f.options || []).map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" data-testid={`input-${f.key}`} />
                    {f.hint || "Ya"}
                  </label>
                ) : (
                  <input type={f.type === "number" ? "number" : "text"} step={f.step || "any"} required={f.required} className="gc-input" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`input-${f.key}`} />
                )}
                {f.hint && f.type !== "checkbox" && <p className="text-[11px] text-slate-400 mt-1">{f.hint}</p>}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 mt-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="gc-btn-ghost">Batal</button>
              <button type="submit" disabled={saving} className="gc-btn-primary" data-testid="btn-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
